/**
 * PDF Font Utility for jsPDF with UTF-8/Turkish character support
 * 
 * This module provides a Roboto font that supports Turkish characters
 * (ş, ğ, ı, ç, ö, ü, Ş, Ğ, İ, Ç, Ö, Ü) for use in PDF exports.
 */

import jsPDF from 'jspdf';

// Cached font data
let regularFontBase64: string | null = null;
let boldFontBase64: string | null = null;
let fontLoadPromise: Promise<void> | null = null;
let fontsLoaded = false;

/**
 * Load and add Roboto font to jsPDF instance
 * This font supports Turkish and other UTF-8 characters
 */
export async function addRobotoFont(pdf: jsPDF): Promise<void> {
  // Lazy load the font data only once
  if (!fontLoadPromise) {
    fontLoadPromise = loadRobotoFontData();
  }
  
  await fontLoadPromise;
  
  if (fontsLoaded && regularFontBase64 && boldFontBase64) {
    // Add fonts directly to this PDF instance
    pdf.addFileToVFS('Roboto-Regular.ttf', regularFontBase64);
    pdf.addFileToVFS('Roboto-Bold.ttf', boldFontBase64);
    pdf.addFont('Roboto-Regular.ttf', 'Roboto', 'normal');
    pdf.addFont('Roboto-Bold.ttf', 'Roboto', 'bold');
  }
}

/**
 * Set Roboto as the default font for the PDF
 */
export function setRobotoFont(pdf: jsPDF, style: 'normal' | 'bold' = 'normal'): void {
  if (fontsLoaded) {
    pdf.setFont('Roboto', style);
  } else {
    // Fallback to helvetica if font not loaded
    pdf.setFont('helvetica', style);
  }
}

/**
 * Load Roboto font data from CDN
 */
async function loadRobotoFontData(): Promise<void> {
  try {
    // Load Roboto Regular and Bold from Google Fonts CDN
    const [regularResponse, boldResponse] = await Promise.all([
      fetch('https://fonts.gstatic.com/s/roboto/v30/KFOmCnqEu92Fr1Me5Q.ttf'),
      fetch('https://fonts.gstatic.com/s/roboto/v30/KFOlCnqEu92Fr1MmWUlvAw.ttf'),
    ]);

    if (!regularResponse.ok || !boldResponse.ok) {
      throw new Error('Failed to fetch Roboto font');
    }

    const [regularBuffer, boldBuffer] = await Promise.all([
      regularResponse.arrayBuffer(),
      boldResponse.arrayBuffer(),
    ]);

    // Convert to base64 and cache
    regularFontBase64 = arrayBufferToBase64(regularBuffer);
    boldFontBase64 = arrayBufferToBase64(boldBuffer);

    fontsLoaded = true;
  } catch (error) {
    console.warn('Failed to load Roboto font, falling back to helvetica:', error);
    fontsLoaded = false;
  }
}

/**
 * Convert ArrayBuffer to base64 string
 */
function arrayBufferToBase64(buffer: ArrayBuffer): string {
  const bytes = new Uint8Array(buffer);
  const len = bytes.byteLength;
  
  // Process in chunks to avoid stack overflow for large files
  const chunkSize = 8192;
  let result = '';
  
  for (let i = 0; i < len; i += chunkSize) {
    const chunk = bytes.subarray(i, Math.min(i + chunkSize, len));
    result += String.fromCharCode.apply(null, Array.from(chunk));
  }
  
  return btoa(result);
}

/**
 * Check if fonts are loaded
 */
export function areFontsLoaded(): boolean {
  return fontsLoaded;
}
